import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import { Refresh, Edit } from '@material-ui/icons'
import CircularProgress from '@material-ui/core/CircularProgress'
import moment from 'moment'
import React from 'react'
import { PainelPendenciaBacenItem } from 'src/api/recebedor-pendencia-bacen'

const useStyles = makeStyles(theme => ({
  requerAcao: {
    '& > td:first-child': {
      boxShadow: `inset 4px 0 0 ${theme.palette.warning.main}`
    },
    '&:hover > td': {
      backgroundColor: theme.palette.warning.light
    }
  },
  naPlataforma: {
    '& > td:first-child': {
      boxShadow: 'inset 4px 0 0 #64B5F6'
    },
    '&:hover > td': {
      backgroundColor: '#E3F2FD'
    }
  },
  ultimaVerificacao: {
    fontWeight: 700
  },
  recebedor: {
    minWidth: 280
  },
  nomeRecebedor: {
    whiteSpace: 'nowrap'
  },
  tenant: {
    minWidth: 220
  },
  medicoVinculado: {
    minWidth: 200
  },
  nomeTenant: {
    whiteSpace: 'nowrap'
  },
  nomeMedico: {
    whiteSpace: 'nowrap'
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5)
  },
  chip: {
    height: 22,
    fontSize: 11,
    fontWeight: 500
  },
  chipPf: {
    '&.MuiChip-root': {
      backgroundColor: '#E3F2FD',
      color: '#1565C0'
    }
  },
  chipPj: {
    '&.MuiChip-root': {
      backgroundColor: '#F3E5F5',
      color: '#6A1B9A'
    }
  },
  chipTipo: {
    '&.MuiChip-root': {
      backgroundColor: '#EEEEEE',
      color: '#616161'
    }
  },
  chipSituacaoPendente: {
    '&.MuiChip-root': {
      backgroundColor: '#FFF8E1',
      color: '#F9A825'
    }
  },
  chipSituacaoRegularizado: {
    '&.MuiChip-root': {
      backgroundColor: '#E8F5E9',
      color: '#2E7D32'
    }
  },
  chipSituacaoRecusado: {
    '&.MuiChip-root': {
      backgroundColor: '#FFEBEE',
      color: '#C62828'
    }
  },
  chipSituacaoFalha: {
    '&.MuiChip-root': {
      backgroundColor: '#FBE9E7',
      color: '#E64A19'
    }
  },
  chipSituacaoForaEscopo: {
    '&.MuiChip-root': {
      backgroundColor: '#ECEFF1',
      color: '#546E7A'
    }
  },
  chipRecebendoSim: {
    '&.MuiChip-root': {
      backgroundColor: '#E8F5E9',
      color: '#2E7D32'
    }
  },
  chipRecebendoNao: {
    '&.MuiChip-root': {
      backgroundColor: '#FFEBEE',
      color: '#C62828'
    }
  }
}))

type Props = {
  itens: PainelPendenciaBacenItem[]
  reverificandoId: string | null
  onReverificar: (item: PainelPendenciaBacenItem) => void
  onAbrirCaso: (pendenciaId: string) => void
}

function formatarData(valor?: string | null) {
  if (!valor) return '—'
  const data = moment(valor)
  return data.isValid() ? data.format('DD/MM/YYYY HH:mm') : '—'
}

function podeRegularizar(item: PainelPendenciaBacenItem) {
  return item.situacao === 'Pendente' || item.situacao === 'RecusadoPagarme'
}

function rotuloTipo(tipo: string, subtipo?: string | null) {
  if (tipo === 'P' && subtipo === 'Hospital') return 'Hospital'
  if (tipo === 'P' && subtipo === 'OPME') return 'OPME'
  if (tipo === 'P') return 'P'
  return tipo || '—'
}

function tituloTipo(tipo: string, subtipo?: string | null) {
  if (tipo === 'M') return 'Médico'
  if (tipo === 'ME') return 'Membro de equipe'
  if (tipo === 'P' && subtipo === 'Hospital') return 'Hospital/Clínica'
  if (tipo === 'P' && subtipo === 'OPME') return 'Fornecedor de OPME'
  if (tipo === 'P') return 'Parceiro'
  if (tipo === 'FA') return 'Fornecedor anestesia'
  if (tipo === 'FS') return 'Fornecedor bolsa de sangue'
  if (tipo === 'SYN') return 'Syn'
  return tipo
}

function rotuloSituacao(situacao: string) {
  if (situacao === 'RecusadoPagarme') return 'Recusado'
  if (situacao === 'FalhaVerificacao') return 'Falha'
  if (situacao === 'ForaDeEscopo') return 'Fora de escopo'
  return situacao
}

export default function TabelaPainelBacen({
  itens,
  reverificandoId,
  onReverificar,
  onAbrirCaso
}: Props) {
  const classes = useStyles()

  function classeSituacao(situacao: string) {
    if (situacao === 'Regularizado') return classes.chipSituacaoRegularizado
    if (situacao === 'Pendente') return classes.chipSituacaoPendente
    if (situacao === 'RecusadoPagarme') return classes.chipSituacaoRecusado
    if (situacao === 'FalhaVerificacao') return classes.chipSituacaoFalha
    if (situacao === 'ForaDeEscopo') return classes.chipSituacaoForaEscopo
    return classes.chipTipo
  }

  function classePessoa(tipoPessoa?: string | null) {
    if (tipoPessoa === 'PF') return classes.chipPf
    if (tipoPessoa === 'PJ') return classes.chipPj
    return classes.chipTipo
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="Painel de pendências BACEN">
        <TableHead>
          <TableRow>
            <TableCell className={classes.recebedor}>Recebedor</TableCell>
            <TableCell className={classes.medicoVinculado}>
              Médico vinculado
            </TableCell>
            <TableCell className={classes.tenant}>Tenant</TableCell>
            <TableCell>Situação</TableCell>
            <TableCell align="right">Pag. 2025</TableCell>
            <TableCell align="right">Pag. 2026</TableCell>
            <TableCell>Recebendo recentemente</TableCell>
            <TableCell>Obrigatórios faltantes</TableCell>
            <TableCell>Opcionais faltantes</TableCell>
            <TableCell>1ª detecção</TableCell>
            <TableCell>Última verificação</TableCell>
            <TableCell>Dias em aberto</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itens.map(item => (
            <TableRow
              key={item.pendenciaId}
              className={
                item.requerAcaoSyn ? classes.requerAcao : classes.naPlataforma
              }
              title={
                item.requerAcaoSyn
                  ? 'Recebedor sem acesso à plataforma: regularize pelo Super Admin.'
                  : 'Seller pode regularizar na plataforma'
              }
            >
              <TableCell className={classes.recebedor}>
                <Typography variant="body2" className={classes.nomeRecebedor}>
                  {item.nome}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {item.documento}
                </Typography>
                <Box className={classes.chips} mt={0.5}>
                  <Tooltip title={tituloTipo(item.tipoRecebedor, item.subtipoParceiro)}>
                    <Chip
                      size="small"
                      label={rotuloTipo(item.tipoRecebedor, item.subtipoParceiro)}
                      className={`${classes.chip} ${classes.chipTipo}`}
                    />
                  </Tooltip>
                  {item.tipoPessoa ? (
                    <Chip
                      size="small"
                      label={item.tipoPessoa}
                      className={`${classes.chip} ${classePessoa(item.tipoPessoa)}`}
                    />
                  ) : null}
                </Box>
              </TableCell>
              <TableCell className={classes.medicoVinculado}>
                {item.tipoRecebedor === 'ME' && item.medicoVinculado ? (
                  <Typography variant="body2" className={classes.nomeMedico}>
                    {item.medicoVinculado}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell className={classes.tenant}>
                <Typography variant="body2" className={classes.nomeTenant}>
                  {item.tenants || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={rotuloSituacao(item.situacao)}
                  className={`${classes.chip} ${classeSituacao(item.situacao)}`}
                />
                {item.dataRegularizacao ? (
                  <Typography variant="caption" color="textSecondary" display="block">
                    {formatarData(item.dataRegularizacao)}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell align="right">{item.qtdPagamentos2025 ?? 0}</TableCell>
              <TableCell align="right">{item.qtdPagamentos2026 ?? 0}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={item.recebendoRecentemente ? 'Sim' : 'Não'}
                  className={`${classes.chip} ${
                    item.recebendoRecentemente
                      ? classes.chipRecebendoSim
                      : classes.chipRecebendoNao
                  }`}
                />
              </TableCell>
              <TableCell>
                {(item.camposObrigatoriosFaltantes || []).join(', ') || '—'}
              </TableCell>
              <TableCell>
                {(item.camposOpcionaisFaltantes || []).join(', ') || '—'}
              </TableCell>
              <TableCell>{formatarData(item.primeiraDeteccao)}</TableCell>
              <TableCell className={classes.ultimaVerificacao}>
                {formatarData(item.ultimaVerificacao)}
              </TableCell>
              <TableCell>{item.diasEmAberto}</TableCell>
              <TableCell align="right">
                <Tooltip title={`Reverificar ${item.nome}`}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Reverificar ${item.nome}`}
                      disabled={reverificandoId === item.pendenciaId}
                      onClick={() => onReverificar(item)}
                    >
                      {reverificandoId === item.pendenciaId ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Refresh fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {podeRegularizar(item) && (
                  <Tooltip title={`Abrir regularização de ${item.nome}`}>
                    <IconButton
                      size="small"
                      aria-label={`Abrir regularização de ${item.nome}`}
                      onClick={() => onAbrirCaso(item.pendenciaId)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
